import React, { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  Close,
  CloudDownload,
  Download,
  OpenInNew,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';

import {
  IKiRegisterAbrufProposal,
  IKiRegisterabrufDatei,
  IKiWeitereListe,
  kiReadsDocuments,
} from '../Interfaces/IKi';
import { IDocument } from '../Interfaces/IDocument';
import { useDownloadRegisterDocuments } from '../hooks/reactQuerys/LegalClientQueries';
import { useDownloadDocument } from '../hooks/reactQuerys/DocumentQueries';
import {
  useGetKiStatus,
  useKiRegisterabruf,
} from '../hooks/reactQuerys/KiQueries';
import { customFetcher } from '../hooks/useFetch';
import { base64ToBlob } from '../LegalTransaction/DocumentDirectory/documentDownload';
import {
  IGesellschafterlisteDatei,
  KiGesellschafterlistePanel,
} from './KiGesellschafterlistePanel';

type IGeladeneDatei = Required<
  Pick<IKiRegisterabrufDatei, 'FileName' | 'MimeType' | 'Data'>
>;

/** Die abgerufene Datei im Browser öffnen oder speichern. */
function dateiZeigen(datei: IGeladeneDatei, herunterladen: boolean) {
  const url = URL.createObjectURL(base64ToBlob(datei.Data, datei.MimeType));

  if (herunterladen) {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = datei.FileName;
    anchor.click();
  } else {
    window.open(url, '_blank', 'noopener');
  }

  // Der neue Tab braucht die Adresse noch einen Moment.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

interface IProps {
  proposal: IKiRegisterAbrufProposal;
}

/**
 * Vorschlagskarte für einen Abruf beim Handelsregister (#1336) —
 * Gesellschafterliste oder Aktueller Abdruck.
 *
 * Die KI hat den Abruf **vorbereitet**: den Mandanten gefunden und geprüft,
 * dass eine Registernummer gepflegt ist. Abgerufen wird erst mit dem Klick,
 * aus der Sitzung des Benutzers über denselben Endpunkt wie im Dialog
 * „Registerdokumente herunterladen" am Mandanten. Das Dokument landet danach
 * am Mandanten — an den Anbieter geht dabei nichts.
 *
 * Firma, Register und Gericht stehen ausgeschrieben da: bei zwei ähnlich
 * benannten Gesellschaften soll die Verwechslung vor dem Klick auffallen.
 */
export const KiRegisterAbrufProposalCard: React.FC<IProps> = ({ proposal }) => {
  const queryClient = useQueryClient();
  const { mutateAsync: abrufen, isPending: isPendingAmMandanten } =
    useDownloadRegisterDocuments();
  const { registerabruf, isPendingRegisterabruf } = useKiRegisterabruf();
  const { downloadDocument, isPending: isPendingDownload } =
    useDownloadDocument();
  const { kiStatus } = useGetKiStatus();

  const [state, setState] = useState<'open' | 'done' | 'dismissed'>('open');
  const [idAbgelegt, setIdAbgelegt] = useState<number | null>(null);
  const [datei, setDatei] = useState<IGeladeneDatei | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Gesellschafterliste: welche gekommen ist und welche das Register sonst
  // noch führt (neueste zuerst) — zum gezielten Nachholen.
  const [listenDatum, setListenDatum] = useState<string | null>(null);
  const [weitere, setWeitere] = useState<IKiWeitereListe[]>([]);
  const [holtDatum, setHoltDatum] = useState<string | null>(null);
  const [weitereFehler, setWeitereFehler] = useState<string | null>(null);

  const isPending = isPendingAmMandanten || isPendingRegisterabruf;
  const ohneMandant = proposal.Payload.OhneMandant;

  if (state === 'dismissed') {
    return null;
  }

  const { Display } = proposal;
  // Eine Karte steht für genau eine Dokumentart (das Werkzeug legt je Art
  // eine eigene an).
  const typ = proposal.Payload.types[0];

  async function handleAbrufOhneMandant() {
    if (ohneMandant === undefined) {
      return;
    }

    const result = await registerabruf(ohneMandant);

    if (result.Data && result.FileName) {
      setDatei({
        FileName: result.FileName,
        MimeType: result.MimeType ?? 'application/pdf',
        Data: result.Data,
      });
      setListenDatum(result.Datum ?? null);
      setWeitere(result.WeitereListen ?? []);
      setState('done');
    } else {
      setError(
        result.Fehler ??
          `Es wurde kein Dokument „${Display.Dokument}" geliefert.`
      );
    }
  }

  async function handleAbruf() {
    setError(null);

    try {
      // Kein Mandant: die Datei kommt zurück, abgelegt wird nichts.
      if (ohneMandant !== undefined || proposal.idLegalClient === null) {
        await handleAbrufOhneMandant();
        return;
      }

      const result = await abrufen({
        idLegalClient: proposal.idLegalClient,
        types: proposal.Payload.types,
        idLegalTransaction: proposal.Payload.idLegalTransaction,
        glDatum: proposal.Payload.glDatum,
      });
      setListenDatum(result?.GLDatum ?? null);
      setWeitere(result?.WeitereGL ?? []);

      await queryClient.invalidateQueries({
        queryKey: ['legalclient/fullObject', proposal.idLegalClient],
      });

      // Der Endpunkt antwortet auch dann mit 200, wenn nichts kam.
      // „Nichts gefunden" ist deshalb kein Erfolg und wird als solcher gesagt.
      const idDocument = result?.[typ];

      if (typeof idDocument === 'number') {
        setIdAbgelegt(idDocument);
        setState('done');
      } else {
        setError(
          result?.Fehler?.[typ] ??
            `Es wurde kein Dokument „${Display.Dokument}" abgelegt.`
        );
      }
    } catch (exception) {
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setError(detail !== '' ? detail : 'Der Abruf ist gescheitert.');
    }
  }

  const fehlerText = (exception: unknown, rueckfall: string) => {
    const detail =
      exception instanceof Error && exception.message
        ? exception.message.replace(/^\d{3} [^:]+: /, '')
        : '';
    return detail !== '' ? detail : rueckfall;
  };

  /** Eine ältere Liste gezielt holen — am Mandanten abgelegt bzw. ohne
   *  Mandant direkt geöffnet. */
  async function holeWeitere(datum: string) {
    setWeitereFehler(null);
    setHoltDatum(datum);

    try {
      if (ohneMandant !== undefined || proposal.idLegalClient === null) {
        if (ohneMandant === undefined) {
          return;
        }
        const result = await registerabruf({ ...ohneMandant, Datum: datum });
        if (result.Data && result.FileName) {
          dateiZeigen(
            {
              FileName: result.FileName,
              MimeType: result.MimeType ?? 'application/pdf',
              Data: result.Data,
            },
            false
          );
        } else {
          setWeitereFehler(result.Fehler ?? 'Die Liste kam nicht.');
        }
        return;
      }

      const result = await abrufen({
        idLegalClient: proposal.idLegalClient,
        types: ['GL'],
        idLegalTransaction: proposal.Payload.idLegalTransaction,
        glDatum: datum,
      });
      await queryClient.invalidateQueries({
        queryKey: ['legalclient/fullObject', proposal.idLegalClient],
      });
      if (typeof result?.GL === 'number') {
        void downloadDocument(result.GL);
      } else {
        setWeitereFehler(result?.Fehler?.GL ?? 'Die Liste kam nicht.');
      }
    } catch (exception) {
      setWeitereFehler(fehlerText(exception, 'Der Abruf ist gescheitert.'));
    } finally {
      setHoltDatum(null);
    }
  }

  const datumDe = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString('de-DE');

  /** „Es gibt noch mehr": die übrigen Listen im Register, gezielt abrufbar. */
  const weitereListen =
    typ === 'GL' && weitere.length > 0 ? (
      <Paper variant='outlined' sx={{ p: 1.5, mt: 1 }}>
        <Typography variant='body2' sx={{ mb: 0.5 }}>
          Im Register {weitere.length === 1 ? 'liegt' : 'liegen'} noch{' '}
          {weitere.length} weitere Gesellschafterliste
          {weitere.length === 1 ? '' : 'n'}:
        </Typography>
        <List dense disablePadding>
          {weitere.map((liste, index) => (
            <ListItem
              key={`ki-gl-weitere-${liste.Datum ?? `ohne-${index}`}`}
              disableGutters
              secondaryAction={
                liste.Datum ? (
                  <Button
                    size='small'
                    startIcon={
                      holtDatum === liste.Datum ? (
                        <CircularProgress size={14} />
                      ) : (
                        <CloudDownload />
                      )
                    }
                    disabled={holtDatum !== null}
                    onClick={() => void holeWeitere(liste.Datum as string)}
                  >
                    {proposal.idLegalClient === null
                      ? 'Abrufen'
                      : 'Abrufen & ablegen'}
                  </Button>
                ) : (
                  <Typography variant='caption' color='text.secondary'>
                    ohne Aufnahmedatum – nicht gezielt abrufbar
                  </Typography>
                )
              }
            >
              <ListItemText
                primary={
                  liste.Datum
                    ? `Liste vom ${datumDe(liste.Datum)}`
                    : liste.Bezeichnung
                }
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
        {weitereFehler !== null && (
          <Alert severity='warning' sx={{ mt: 1 }}>
            {weitereFehler}
          </Alert>
        )}
      </Paper>
    ) : null;

  /** „… vom 17.04.2026" an der Erfolgsmeldung, wenn bekannt. */
  const vom =
    typ === 'GL' && listenDatum ? ` (Liste vom ${datumDe(listenDatum)})` : '';

  // Nach einer Gesellschafterliste: anbieten, die Gesellschafter anzulegen —
  // nur, wenn der eingestellte Anbieter Dokumente liest (kein anderer).
  const gesellschafterAnbieten = typ === 'GL' && kiReadsDocuments(kiStatus);

  const gesellschafterPanel = (
    dateiHolen: () => Promise<IGesellschafterlisteDatei>
  ) =>
    gesellschafterAnbieten && (
      <KiGesellschafterlistePanel
        firma={Display.Firma}
        idLegalClient={proposal.idLegalClient}
        dateiHolen={dateiHolen}
        anbieter={kiStatus?.ProviderLabel}
      />
    );

  if (state === 'done' && datei !== null) {
    return (
      <Stack>
        <Alert
          severity='success'
          icon={<AccountBalance fontSize='inherit' />}
          action={
            <Stack direction='row' spacing={0.5}>
              <Button
                size='small'
                color='inherit'
                startIcon={<OpenInNew />}
                onClick={() => dateiZeigen(datei, false)}
              >
                Öffnen
              </Button>
              <Button
                size='small'
                color='inherit'
                startIcon={<Download />}
                onClick={() => dateiZeigen(datei, true)}
              >
                Herunterladen
              </Button>
            </Stack>
          }
        >
          {Display.Dokument} von {Display.Firma}
          {vom} ist abgerufen — nicht in DiNo abgelegt.
        </Alert>
        {weitereListen}
        {gesellschafterPanel(() => Promise.resolve(datei))}
      </Stack>
    );
  }

  /** Die am Mandanten abgelegte Liste für das Auslesen nachladen. */
  async function abgelegteListe(): Promise<IGesellschafterlisteDatei> {
    const doc: IDocument = await customFetcher({
      endpoint: `/document/${idAbgelegt}`,
      method: 'GET',
    });

    if (!doc?.Data) {
      throw new Error('Die abgelegte Liste konnte nicht geladen werden.');
    }

    return {
      FileName: doc.FileName,
      MimeType: doc.FileName.toLowerCase().endsWith('.pdf')
        ? 'application/pdf'
        : 'application/octet-stream',
      Data: doc.Data,
    };
  }

  if (state === 'done') {
    return (
      <Stack>
        <Alert
          severity='success'
          icon={<AccountBalance fontSize='inherit' />}
          action={
            idAbgelegt !== null && (
              <Button
                size='small'
                color='inherit'
                startIcon={<Download />}
                disabled={isPendingDownload}
                onClick={() => void downloadDocument(idAbgelegt)}
              >
                Öffnen
              </Button>
            )
          }
        >
          {Display.Dokument} von {Display.Firma}
          {vom} liegt jetzt am Mandanten.
        </Alert>
        {weitereListen}
        {idAbgelegt !== null && gesellschafterPanel(abgelegteListe)}
      </Stack>
    );
  }

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderColor: 'primary.light' }}>
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <AccountBalance fontSize='small' color='primary' />
          <Typography variant='subtitle2'>
            {Display.Dokument} abrufen
          </Typography>
        </Stack>

        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
          <Chip size='small' label={Display.Firma} />
          {Display.ListenDatum && (
            <Chip
              size='small'
              color='primary'
              variant='outlined'
              label={`Liste vom ${datumDe(Display.ListenDatum)}`}
            />
          )}
          <Chip size='small' variant='outlined' label={Display.Register} />
          <Chip
            size='small'
            variant='outlined'
            label={
              Display.Registergericht
                ? `AG ${Display.Registergericht}`
                : 'kein Registergericht gepflegt'
            }
          />
          {Display.Vorgang && (
            <Chip
              size='small'
              color='primary'
              variant='outlined'
              label={`Vorgang: ${Display.Vorgang}`}
            />
          )}
        </Stack>

        {Display.VorhandenArray.length > 0 && (
          <div>
            <Typography variant='caption' color='text.secondary'>
              Bereits am Mandanten
            </Typography>
            <List dense disablePadding>
              {Display.VorhandenArray.map(doc => (
                <ListItem
                  key={`ki-gl-${doc.idDocument}`}
                  disableGutters
                  secondaryAction={
                    <Tooltip title='Herunterladen'>
                      <IconButton
                        size='small'
                        disabled={isPendingDownload}
                        onClick={() => void downloadDocument(doc.idDocument)}
                      >
                        <Download fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={doc.FileName ?? `Dokument ${doc.idDocument}`}
                    secondary={doc.Created_at ?? undefined}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        )}

        {proposal.Hinweise.length > 0 && (
          <Alert severity='info' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {proposal.Hinweise.map(hinweis => (
                <Typography component='li' variant='caption' key={hinweis}>
                  {hinweis}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {error !== null && <Alert severity='warning'>{error}</Alert>}

        {isPending && (
          <Stack direction='row' spacing={1} alignItems='center'>
            <CircularProgress size={16} />
            <Typography variant='caption' color='text.secondary'>
              {/* Gemessen 08.10.2026: AD gut 20 s; GL um 90 s, davon fast
                  eine Minute, bis das Portal die Dokumentenansicht liefert. */}
              {typ === 'GL'
                ? 'Das Registerportal wird abgefragt — bei der Gesellschafterliste dauert das etwa eineinhalb Minuten.'
                : 'Das Registerportal wird abgefragt — das dauert etwa eine halbe Minute.'}
            </Typography>
          </Stack>
        )}

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            size='small'
            color='inherit'
            startIcon={<Close />}
            disabled={isPending}
            onClick={() => setState('dismissed')}
          >
            Verwerfen
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={<CloudDownload />}
            disabled={isPending}
            onClick={() => void handleAbruf()}
          >
            {error !== null ? 'Erneut abrufen' : 'Abrufen'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
